import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";
import { useSmoothScroll } from "./SmoothScroll";

const CountUp = ({ to = 150000000 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [val, setVal] = useState(to);
  const played = useRef(false);

  useEffect(() => {
    if (!inView || played.current) return;
    played.current = true;
    setVal(0);
    const controls = animate(0, to, {
      duration: 2.4,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(Math.floor(v)),
      onComplete: () => setVal(to),
    });
    return () => controls.stop();
  }, [inView, to]);

  return <span ref={ref}>{val.toLocaleString("en-US")}</span>;
};

const StatCard = ({ index, label, note, featured, children, testId }) => (
  <div
    className={`group relative h-full overflow-hidden rounded-2xl border p-8 sm:p-10 backdrop-blur-xl transition-all duration-500 ${
      featured
        ? "border-[#D4AF37]/40 bg-[#14141A]/90 shadow-[0_8px_32px_rgba(0,0,0,0.5),0_0_60px_rgba(212,175,55,0.08)]"
        : "border-white/10 bg-[#14141A]/80 shadow-[0_8px_32px_rgba(0,0,0,0.5)] hover:border-[#D4AF37]/30"
    }`}
    data-testid={testId}
  >
    <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.12),transparent_70%)] opacity-0 transition-opacity duration-700 group-hover:opacity-100" aria-hidden="true" />
    <div className="flex items-center justify-between">
      <span className="font-mono text-sm text-[#D4AF37]">{index}</span>
      <span className="h-px flex-1 ml-4 bg-white/[0.08]" aria-hidden="true" />
    </div>
    <div className="mt-8">{children}</div>
    <p className="mt-5 font-mono text-[11px] tracking-[0.28em] text-[#99A1AF] uppercase">{label}</p>
    {note && <p className="mt-3 text-[11px] leading-relaxed text-[#636B78]">{note}</p>}
  </div>
);

export default function WinToken() {
  const scrollTo = useSmoothScroll();

  return (
    <section id="win-token" className="relative overflow-hidden py-24 sm:py-32" data-testid="win-token-section">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_20%,rgba(212,175,55,0.09),transparent_60%)]" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          align="center"
          chapter="Chapter 01"
          kicker="Token Scarcity"
          title="WIN Token"
          subtitle="Limited Supply. Built for a Growing Ecosystem."
        />

        <div className="mt-16 grid gap-5 md:grid-cols-3">
          <Reveal>
            <StatCard index="01" label="Limited Total Supply" featured testId="token-stat-supply">
              <div className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-gold-gradient" data-testid="token-scarcity-figure">
                <CountUp />
              </div>
              <p className="mt-2 font-display text-base font-semibold tracking-[0.3em] text-white">WIN</p>
            </StatCard>
          </Reveal>

          <Reveal delay={0.1}>
            <StatCard
              index="02"
              label="Estimated Launch Price"
              note="An estimate only. Not a guaranteed future market price."
              testId="token-stat-launch-price"
            >
              <div className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white">$10</div>
              <p className="mt-2 font-display text-base font-semibold tracking-[0.12em] text-[#99A1AF]">~ ₹1,000</p>
            </StatCard>
          </Reveal>

          <Reveal delay={0.2}>
            <StatCard
              index="03"
              label="Current WIN Price"
              note="Shown for reference. Digital asset prices can move in either direction."
              testId="token-stat-current-price"
            >
              <div className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white">₹12</div>
              <p className="mt-2 font-display text-base font-semibold tracking-[0.12em] text-[#99A1AF]">per WIN</p>
            </StatCard>
          </Reveal>
        </div>

        <Reveal delay={0.25}>
          <p className="mx-auto mt-12 max-w-2xl text-center text-sm sm:text-base leading-relaxed text-[#99A1AF]">
            WIN Token has a limited total supply of 150 million tokens and is designed to support
            the expanding Winter Protocol ecosystem.
          </p>
        </Reveal>

        <Reveal delay={0.3}>
          <div className="mt-8 flex justify-center">
            <div
              className="inline-flex items-center gap-3 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] px-6 py-3"
              data-testid="daily-rewards-highlight"
            >
              <Sparkles className="h-4 w-4 shrink-0 text-[#D4AF37]" />
              <span className="text-sm font-medium text-[#F3E5AB]">Claim Daily Unlimited Rewards</span>
              <span className="hidden sm:inline text-xs text-[#99A1AF]">— claim every day, no daily cap</span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.35}>
          <div className="mt-8 text-center">
            <button
              onClick={() => scrollTo?.("#ecosystem")}
              data-testid="token-explore-button"
              className="group inline-flex items-center gap-2.5 rounded-full border border-[#D4AF37]/40 px-8 py-3.5 text-sm font-medium text-[#F3E5AB] transition-all duration-300 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/70"
            >
              Explore WIN Token
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
